type EventHandler = (data: any) => void;

interface SseClientConfig {
  url: string;
  token: string;
  body: Record<string, unknown>;
}

/**
 * Cliente SSE custom — usa fetch + ReadableStream porque o EventSource nativo
 * não suporta POST com body nem headers customizados.
 */
export class SseClient {
  private handlers = new Map<string, EventHandler[]>();

  constructor(private config: SseClientConfig) {}

  on(event: string, handler: EventHandler): this {
    const list = this.handlers.get(event) ?? [];
    list.push(handler);
    this.handlers.set(event, list);
    return this;
  }

  async start(): Promise<void> {
    // try/catch cobre falha do fetch (offline/DNS/CORS/TLS) E queda no meio do
    // stream (reader.read) — senão a promise rejeita silenciosa e o chamador
    // fica preso "streaming" pra sempre. Em qualquer erro, emite 'error'.
    try {
      const resp = await fetch(this.config.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          Authorization: `Bearer ${this.config.token}`,
        },
        body: JSON.stringify(this.config.body),
      });

      if (!resp.ok || !resp.body) {
        this.emit('error', { detail: `HTTP ${resp.status}` });
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // sse-starlette usa \r\n\r\n como separador — regex aceita LF e CRLF
        const events = buffer.split(/\r?\n\r?\n/);
        buffer = events.pop() ?? '';

        for (const raw of events) {
          const parsed = this.parseSseEvent(raw);
          if (parsed) this.emit(parsed.event, parsed.data);
        }
      }
    } catch {
      this.emit('error', { detail: 'Erro de conexão. Tente de novo.' });
    }
  }

  private parseSseEvent(raw: string): { event: string; data: any } | null {
    // Aceita CRLF e LF (sse-starlette manda \r\n nas linhas)
    const lines = raw.split(/\r?\n/);
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of lines) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    }
    if (dataLines.length === 0) return null;
    try {
      return { event, data: JSON.parse(dataLines.join('\n')) };
    } catch {
      return null;
    }
  }

  private emit(event: string, data: any): void {
    const list = this.handlers.get(event);
    if (list) for (const h of list) h(data);
  }
}
